import { useEffect, useState } from 'react';

import { load as loadBotd } from '@fingerprintjs/botd';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { consoleInfoLog, isMobile } from '../Helpers';

/* --- helpers & checks --- */
const UA = () => navigator.userAgent;

const botUARegexes = [
    /bot/i,
    /crawl/i,
    /spider/i,
    /slurp/i,
    /mediapartners/i,
    /baiduspider/i,
    /yandex/i,
    /duckduckbot/i,
    /bingbot/i,
    /googlebot/i,
    /crawler/i,
    /curl/i,
    /wget/i,
    /headless/i,
];

const detectUserAgentBot = () => botUARegexes.some((re) => re.test(UA()));
const detectProcessPresence = () => typeof window.process !== 'undefined';

const detectNoWebRTC = () =>
    typeof RTCPeerConnection === 'undefined' &&
    typeof globalThis.webkitRTCPeerConnection === 'undefined';

const detectMimeTypesConsistency = () =>
    !navigator.mimeTypes || typeof navigator.mimeTypes.length !== 'number';

const detectLowColorDepth = () =>
    screen?.colorDepth > 0 && screen.colorDepth < 24;

const detectDeviceMemorySpoof = () => {
    const mem = navigator.deviceMemory;

    return typeof mem === 'number' && mem < 0.25;
};

const detectPerformance = () => {
    try {
        return !performance.getEntriesByType('navigation').length;
    } catch {
        return false;
    }
};

const detectHeadless = () => {
    const ua = UA().toLowerCase();

    // Confirmed automation: Selenium/Puppeteer/Playwright or explicit headless UA.
    return (
        navigator.webdriver === true ||
        /headlesschrome|headlessfirefox|phantomjs/.test(ua)
    );
};

// In-app browsers / WebViews where many "desktop browser" assumptions break.
const EMBEDDED_UA = /(FBAN|FBAV|Instagram|Snapchat|TikTok|Line|GSA|WebView|; wv)/i;

const detectHeadlessHeuristic = () => {
    if (detectHeadless()) return false;

    // Zero physical dimensions is a strong headless tell for a real interactive
    // browser, but skip embedded/in-app contexts where these can legitimately be 0.
    if (EMBEDDED_UA.test(UA())) return false;

    return (
        (!window.outerWidth && !window.outerHeight) ||
        (window.screen &&
            (window.screen.width === 0 || window.screen.height === 0))
    );
};

// `window.chrome` exists on desktop Chrome and Android Chrome (Blink), but NOT on
// iOS Chrome (CriOS / WebKit), nor reliably inside in-app/WebView contexts. Only a
// weak joint signal when missing on a platform where it is genuinely expected.
const detectNoChromeObject = () => {
    const ua = UA();
    const isIOS = /iPhone|iPad|iPod|CriOS/i.test(ua);
    const isDesktopOrAndroidChrome =
        /Chrome|Chromium/i.test(ua) &&
        !/Edg|OPR|SamsungBrowser/i.test(ua) &&
        !isIOS &&
        !EMBEDDED_UA.test(ua);

    return isDesktopOrAndroidChrome && typeof window.chrome === 'undefined';
};

const detectNavigatorProps = () => {
    const hasLangs =
        Array.isArray(navigator.languages) && navigator.languages.length > 0;
    const hasHC =
        typeof navigator.hardwareConcurrency === 'number' &&
        navigator.hardwareConcurrency > 0;

    // webdriver is covered by Headless; only flag missing core navigator data here.
    return !(hasLangs && hasHC);
};

const detectWebGLRenderer = () => {
    try {
        const canvas = document.createElement('canvas');
        const gl =
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl');

        if (!gl) return false;

        const renderer = gl.getParameter(gl.RENDERER);

        return !!(
            renderer &&
            /swiftshader|llvmpipe|software rasterizer/i.test(renderer)
        );
    } catch {
        return false;
    }
};

const detectCanvasSpoofing = () => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx?.fillText('x', 1, 1);
        const data = canvas.toDataURL();

        return !data || data.length < 100;
    } catch {
        return false;
    }
};

const detectNoTouchSupport = () => {
    if (!isMobile()) return false;

    return !(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        ('msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0)
    );
};
const detectNoPlugins = () => {
    try {
        if (isMobile()) return false;

        const ua = UA();
        // Chromium 88+ always reports zero plugins; not a useful bot signal.
        if (/Chrome|Chromium|Edg|OPR/i.test(ua) && !/Firefox/i.test(ua)) {
            return false;
        }

        return (
            'plugins' in navigator &&
            navigator.plugins &&
            navigator.plugins.length === 0
        );
    } catch {
        return false;
    }
};

const detectLangTimezoneMissing = () => {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return !navigator.language || !timeZone;
    } catch {
        return false;
    }
};

const detectProductSubSpoof = () => {
    try {
        const ua = navigator.userAgent || '';
        // Only flag on Blink/WebKit (Chrome/Safari family). Firefox uses 20100101 normally.
        const isBlinkOrWebKit =
            /(Chrome|Chromium|CriOS|Safari)/i.test(ua) && !/Firefox/i.test(ua);
        if (!isBlinkOrWebKit) return false;

        return !!(navigator.productSub && navigator.productSub !== '20030107');
    } catch {
        return false;
    }
};

const detectPushPermissionAnomaly = () => {
    try {
        const ua = navigator.userAgent || '';

        // Legit environments where Notification can be missing
        const isIOS = /iPhone|iPad|iPod/i.test(ua); // iOS Safari <16.4, many WKWebViews
        const isLiteBrowser = /Opera Mini|UCBrowser|Puffin/i.test(ua);
        const isInAppBrowser = /(FBAN|FBAV|Instagram|Snapchat|TikTok)/i.test(
            ua
        );
        const isOldAndroidWebView =
            /Android [0-4]\./i.test(ua) && /wv/.test(ua);

        const notif = globalThis.Notification;

        // The Notification API requires a secure context; its absence over HTTP
        // (or in sandboxed/insecure frames) is expected, not a bot signal.
        if (
            typeof notif === 'undefined' &&
            globalThis.isSecureContext === false
        ) {
            return false;
        }

        // Acceptable missing Notification on these platforms
        if (
            (isIOS || isLiteBrowser || isInAppBrowser || isOldAndroidWebView) &&
            typeof notif === 'undefined'
        ) {
            return false;
        }

        if (
            typeof notif === 'undefined' ||
            typeof notif.permission !== 'string'
        ) {
            return true; // suspicious elsewhere
        }

        return !['default', 'granted', 'denied'].includes(notif.permission);
    } catch {
        return false; // Fail open: an exception here is not evidence of a bot.
    }
};

/* -------------------- Level 1: high-precision, low-FP signals -------------------- */

// Residue that automation frameworks inject into the page context. These names do
// not occur on genuine user-launched browsers, so a hit is near-certain automation.
const AUTOMATION_RESIDUE_KEYS = [
    '$cdc_asdjflasutopfhvcZLmcfl_',
    'cdc_adoQpoasnfa76pfcZLmcfl_Array',
    'cdc_adoQpoasnfa76pfcZLmcfl_Promise',
    'cdc_adoQpoasnfa76pfcZLmcfl_Symbol',
    '__playwright__binding__',
    '__pwInitScripts',
    '__puppeteer_evaluation_script__',
    '__selenium_unwrapped',
    '__webdriver_evaluate',
    '__webdriver_script_fn',
    '__webdriver_script_func',
    '__driver_evaluate',
    '__fxdriver_evaluate',
    '_Selenium_IDE_Recorder',
    '_phantom',
    'callPhantom',
    'domAutomation',
    'domAutomationController',
];

const detectCDPArtifact = () => {
    try {
        const win = window;
        const doc = document;

        for (const key of AUTOMATION_RESIDUE_KEYS) {
            if (key in win || key in doc) return true;
        }

        // ChromeDriver/Selenium inject prefixed keys with a random suffix.
        const hasPrefixed = Object.keys(win).some(
            (k) => /^[$]?cdc_/.test(k) || /^[$]wdc_/.test(k)
        );
        if (hasPrefixed) return true;

        const root = doc.documentElement;
        if (
            root &&
            (root.getAttribute('webdriver') !== null ||
                root.getAttribute('selenium') !== null ||
                root.getAttribute('driver') !== null)
        ) {
            return true;
        }

        return false;
    } catch {
        return false;
    }
};

const isNativeFn = (fn) => {
    try {
        return (
            typeof fn === 'function' &&
            /\{\s*\[native code\]\s*\}/.test(
                Function.prototype.toString.call(fn)
            )
        );
    } catch {
        return false;
    }
};

// Stealth plugins force `navigator.webdriver` to `false` by installing an own
// accessor on the navigator instance. Genuine Chromium defines it on the prototype,
// so an own getter (especially a non-native one) is a tampering tell.
const detectWebdriverTampering = () => {
    try {
        const ownDesc = Object.getOwnPropertyDescriptor(
            navigator,
            'webdriver'
        );

        if (ownDesc && typeof ownDesc.get === 'function') {
            return !isNativeFn(ownDesc.get);
        }

        return false;
    } catch {
        return false;
    }
};

// A gross contradiction between the OS claimed in the UA string and the OS reported
// by navigator.platform / Client Hints is a strong, low-FP automation/spoof signal.
const detectUAPlatformMismatch = () => {
    try {
        const ua = UA();
        const navPlatform = (navigator.platform || '').toLowerCase();
        const chPlatform = (
            navigator.userAgentData?.platform || ''
        ).toLowerCase();

        const uaSaysWindows = /Windows NT/i.test(ua);
        const uaSaysMac = /Macintosh|Mac OS X/i.test(ua);
        const uaSaysAndroid = /Android/i.test(ua);
        const uaSaysIOS = /iPhone|iPad|iPod/i.test(ua);

        if (navPlatform) {
            if (uaSaysWindows && !navPlatform.startsWith('win')) return true;
            if (uaSaysMac && !navPlatform.startsWith('mac')) return true;
            if (
                uaSaysIOS &&
                !/iphone|ipad|ipod|mac/.test(navPlatform)
            )
                return true;
        }

        if (chPlatform) {
            if (uaSaysWindows && chPlatform !== 'windows') return true;
            if (uaSaysMac && chPlatform !== 'macos') return true;
            if (uaSaysAndroid && chPlatform !== 'android') return true;
        }

        return false;
    } catch {
        return false;
    }
};

// Headless Chrome auto-denies notifications, producing a contradiction between the
// Permissions API state ('prompt') and Notification.permission ('denied').
const detectPermissionContradiction = async () => {
    try {
        const notif = globalThis.Notification;

        if (!notif || typeof navigator.permissions?.query !== 'function') {
            return false;
        }

        const status = await navigator.permissions.query({
            name: 'notifications',
        });

        return notif.permission === 'denied' && status.state === 'prompt';
    } catch {
        return false;
    }
};

/* -------------------- Browser classification -------------------- */
const getBrowserFlags = () => {
    const ua = UA();

    return {
        isChrome:
            /chrome|crios|chromium/i.test(ua) &&
            !/\sOPR\/|\sEdg\//.test(ua) &&
            !/SamsungBrowser\//i.test(ua),
        isFirefox: /firefox/i.test(ua),
        isSafari:
            /safari/i.test(ua) &&
            !/chrome|chromium|crios|android|opr|edg|samsungbrowser/i.test(ua),
        isEdge: /\sEdg\//.test(ua),
        isOpera: /\sOPR\//.test(ua),
        isSamsung: /SamsungBrowser\//i.test(ua),
    };
};

function detectFontsSignals(components, ua = navigator.userAgent) {
    const isFirefox = /firefox/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroidWV = /Android/i.test(ua) && /\bwv\b/.test(ua);
    const isChromiumDesktop =
        /Chrome|Chromium/i.test(ua) &&
        !/Mobile/i.test(ua) &&
        !/Edg|OPR|SamsungBrowser/i.test(ua);

    // Skip environments where fonts are commonly restricted or noisy
    if (isFirefox || isIOS || isAndroidWV || !isChromiumDesktop) {
        return { fired: false, label: '' };
    }

    const fonts = components?.fonts?.value;
    const fontsLen = Array.isArray(fonts) ? fonts.length : 0;

    const fontPrefs = components?.fontPreferences?.value;
    const hasFontPrefs =
        fontPrefs &&
        typeof fontPrefs === 'object' &&
        Object.keys(fontPrefs).length > 0;

    // STRONG: both missing where we expect them
    if (fontsLen === 0 && !hasFontPrefs) {
        return { fired: true, label: 'FontsMissing' };
    }

    // WEAK: only font preferences missing
    if (!hasFontPrefs) {
        return { fired: true, label: 'FontPrefsMissing' };
    }

    return { fired: false, label: '' };
}

/* -------------------- Scoring model -------------------- */
/**
 * Signal weights (best-practice tuning):
 * - 1 (weak): common in privacy browsers, VMs, or modern Chromium quirks
 * - 2 (strong): meaningful automation / spoofing evidence
 * - 3 (critical): high-confidence automation; also listed in INSTANT_SUSPECTED
 *
 * Verdict tiers:
 * - Bot: UA bot signature, confirmed headless automation, OR score >= threshold + 2
 * - BotSuspected: instant-suspected signal (incl. CDP residue) OR score >= threshold
 * - Human: otherwise
 */
const SIGNAL_WEIGHT = {
    // Weak (+1)
    NoPlugins: 1,
    AudioZero: 1,
    WebGLSoftware: 1,
    PerfAPIEmpty: 1,
    NoWebRTC: 1,
    DeviceMemorySpoof: 1,
    MimeTypesInconsistent: 1,
    LowColorDepth: 1,
    PushPermissionAnomaly: 1,
    FontPrefsMissing: 1,
    NavProps: 1,
    LowFingerprintConfidence: 1,
    NoChromeObject: 1,

    // Strong (+2)
    LangTimeMismatch: 2,
    NoTouchSupport: 2,
    CanvasFingerprint: 2,
    FontsMissing: 2,
    ProductSubSpoof: 2,
    ProcessDetected: 2,
    HeadlessHeuristic: 2,
    WebdriverTampering: 2,
    UAPlatformMismatch: 2,
    PermissionContradiction: 2,

    // Critical (+3)
    Headless: 3,
    CDPArtifact: 3,
};

const DEFAULT_SIGNAL_WEIGHT = 2;

// Definitive bot: UA admits crawler/automation, or confirmed headless driver.
const INSTANT_BOT = new Set(['BotSignature', 'Headless']);

// High-confidence automation: never classify as human when present alone.
// CanvasFingerprint and ProductSubSpoof were demoted from this set because privacy
// tools and uncommon browsers can trip them; they still contribute their weight.
const INSTANT_SUSPECTED = new Set([
    'HeadlessHeuristic',
    'ProcessDetected',
    'FontsMissing',
    'CDPArtifact',
]);

const LOW_FINGERPRINT_CONFIDENCE = 0.35;

const thresholdFor = (b) => {
    if (b.isSafari || b.isFirefox) return 4; // privacy-heavy browsers
    if (b.isEdge || b.isOpera || b.isSamsung) return 3;

    return 3; // Chrome baseline
};

const signalWeight = (label) => SIGNAL_WEIGHT[label] ?? DEFAULT_SIGNAL_WEIGHT;

const internalScore = (labels) =>
    labels.reduce((score, label) => {
        if (String(label).startsWith('Confidence')) return score;

        return score + signalWeight(label);
    }, 0);

const labelForScore = (score, threshold, labels) => {
    if (labels.some((l) => INSTANT_BOT.has(l))) return 'Bot';

    const hasInstantSuspected = labels.some((l) => INSTANT_SUSPECTED.has(l));

    if (score >= threshold + 2) return 'Bot';
    if (score >= threshold || hasInstantSuspected) return 'BotSuspected';

    return 'Human';
};

/** Maps internal score label to hook return value for custom heuristics only. */
const scoreLabelToCustomIsBot = (label) => {
    if (label === 'Bot') return 'bot';
    if (label === 'BotSuspected') return 'suspected';

    return 'human';
};

export function useCustomDetectBot() {
    const [isBot, setIsBot] = useState(null);
    const [customIsBot, setCustomIsBot] = useState(null);
    const [visitorId, setVisitorId] = useState(null);
    const [failedChecks, setFailedChecks] = useState([]);

    useEffect(() => {
        let cancelled = false;

        (async function run() {
            // --- PUPPETEER PREBUILD SHORT-CIRCUIT ---
            if (window.__PUPPETEER_PREBUILD_MODE__) {
                if (!cancelled) {
                    setIsBot(false);
                    setCustomIsBot('human');
                    setFailedChecks([]);
                }
                return;
            }

            try {
                // BotD (primary) and FPJS (auxiliary) are independent: run them
                // concurrently so the gated offer fetch waits on the slower of the
                // two rather than their sum.
                const [botdResult, fpResult] = await Promise.all([
                    loadBotd().then((botd) => botd.detect()),
                    FingerprintJS.load().then((fp) => fp.get()),
                ]);

                const {
                    visitorId: vid,
                    components,
                    confidence,
                } = fpResult;

                if (cancelled) return;

                setVisitorId(vid);

                const browser = getBrowserFlags();
                const labels = [];

                // Confidence (tracked for analytics; low values add a weak score signal)
                labels.push(`Confidence${confidence?.score ?? 'N/A'}`);

                if (
                    typeof confidence?.score === 'number' &&
                    confidence.score < LOW_FINGERPRINT_CONFIDENCE
                ) {
                    labels.push('LowFingerprintConfidence');
                }

                const fontSig = detectFontsSignals(components);

                if (fontSig.fired) labels.push(fontSig.label);

                if (
                    components?.audio &&
                    typeof components.audio.value === 'number' &&
                    components.audio.value === 0
                )
                    labels.push('AudioZero');

                // Async signal: notification permission contradiction (headless tell).
                if (await detectPermissionContradiction()) {
                    labels.push('PermissionContradiction');
                }

                const checks = [
                    ['Headless', detectHeadless],
                    ['HeadlessHeuristic', detectHeadlessHeuristic],
                    ['NoChromeObject', detectNoChromeObject],
                    ['CDPArtifact', detectCDPArtifact],
                    ['WebdriverTampering', detectWebdriverTampering],
                    ['UAPlatformMismatch', detectUAPlatformMismatch],
                    ['BotSignature', detectUserAgentBot],
                    ['PerfAPIEmpty', detectPerformance],
                    ['NavProps', detectNavigatorProps],
                    ['WebGLSoftware', detectWebGLRenderer],
                    ['CanvasFingerprint', detectCanvasSpoofing],
                    ['LangTimeMismatch', detectLangTimezoneMissing],
                    ['NoWebRTC', detectNoWebRTC],
                    ['ProcessDetected', detectProcessPresence],
                    ['PushPermissionAnomaly', detectPushPermissionAnomaly],
                    ['MimeTypesInconsistent', detectMimeTypesConsistency],
                    ['LowColorDepth', detectLowColorDepth],
                    ['NoPlugins', detectNoPlugins],
                    ['NoTouchSupport', detectNoTouchSupport],
                    ['ProductSubSpoof', detectProductSubSpoof],
                    ['DeviceMemorySpoof', detectDeviceMemorySpoof],
                ];

                for (const [label, fn] of checks) {
                    try {
                        if (fn()) labels.push(label);
                    } catch (error) {
                        captureCustomException(error, 'BotCheckError', {
                            check_failed: label,
                        });
                    }
                }

                const score = internalScore(labels);
                const threshold = thresholdFor(browser);
                const label = labelForScore(score, threshold, labels);

                const botdFlag = !!botdResult?.bot;
                const customTier = scoreLabelToCustomIsBot(label);
                const finalFailedChecks = [
                    ...labels,
                    ...(label === 'Bot' ? ['Bot'] : []),
                    ...(label === 'BotSuspected' ? ['BotSuspected'] : []),
                ];

                if (!cancelled) {
                    consoleInfoLog(
                        `## Bot Detector Data: visitorId - ${vid}, isBot (BotD) - ${botdFlag}, customIsBot - ${customTier}, failedChecks - ${finalFailedChecks}`,
                        'init'
                    );
                    setIsBot(botdFlag);
                    setCustomIsBot(customTier);
                    setFailedChecks(finalFailedChecks);
                }
            } catch (e) {
                if (!cancelled) {
                    setIsBot(false);
                    setCustomIsBot('unknown');
                    setFailedChecks(['DetectionError']);
                    captureCustomException(e, 'BotDetectionError');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { visitorId, isBot, customIsBot, failedChecks };
}
